import React from "react";
import { useParams } from "react-router-dom";
import { Grid, Box, Button } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { getJobExecutionsForVehicles } from "../../services/vehicle";
import { getDeploymentVehicleLogDetails } from "../../services/deployment";
import { getColumnsData } from "./VehicleDeploymentColumns";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";

const VehicleDeploymentList = (props = {}) => {
  let { vehicleId, deploymentId } = useParams();
  useQueryClient();
  const {
    vehicles = [],
    type,
    setErrMsg = () => { },
    setShowJobDocument,
    deploymentVersion,
    CustomToolbar,
  } = props;

  const [vehicle] = vehicles;

  const getFn =
    type === "vehicle"
      ? getJobExecutionsForVehicles
      : getDeploymentVehicleLogDetails;
  const queryArray =
    type === "vehicle"
      ? ["jobExecutionsForVehicle", deploymentId, vehicle?.vehicleKey]
      : ["vehicleLogDetails", deploymentId];

  const columns = getColumnsData({
    vehicleId,
    deploymentId,
    type,
    deploymentVersion,
  });

  // jobExecutions Queries
  let { data: { data: allVehicles = [] } = {}, isLoading: summaryLoading } =
    useQuery(
      queryArray,
      getFn, // to get the execution summary of the whole deployment
      {
        refetchOnWindowFocus: true,
        retry: 0,
        refetchInterval: 5000,
        onSuccess: () => {
          setShowJobDocument?.(true);
        },
        onError: (error) => {
          const { response: { data: { message = "" } = {} } = {} } = error;
          setErrMsg(message);
        },
      }
    );

  // Export to CSV function
  const handleExportCSV = () => {
    if (!allVehicles || allVehicles.length === 0) return;

    // Get visible column fields
    const visibleColumns = columns.filter(
      (col) => col.field !== "none" && (!vehicleId || col.field !== "vehicleKey")
    );

    // Create CSV header
    const headers = visibleColumns.map((col) => col.headerName).join(",");

    // Create CSV rows
    const rows = allVehicles.map((row) => {
      return visibleColumns
        .map((col) => {
          let value = row[col.field] || "";

          // Handle special formatting
          if (col.field === "serialNo" || col.field === "vehicleKey" || col.field === "vehicleGroupName") {
            value = row[col.field] || "";
          } else if (col.field === "jobExecutionStatus") {
            value = row.jobExecutionStatus || "";
          } else if (col.field === "createdAt" || col.field === "updatedAt") {
            value = row[col.field] || "";
          }

          // Escape commas and quotes in values
          if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }

          return value;
        })
        .join(",");
    });

    // Combine headers and rows
    const csv = [headers, ...rows].join("\n");

    // Create and download file
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `deployment-vehicles-export.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //Main component
  return (
    <Grid item xs={12}>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={!allVehicles || allVehicles.length === 0}
        >
          Export CSV
        </Button>
      </Box>
      <Box
        sx={
          type === "vehicle"
            ? {
              height: "auto",
              width: "100%",
            }
            : {}
        }
      >
        <DataGrid
          columnVisibilityModel={{
            vehicleKey: !vehicleId ? true : false,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
          sx={
            type === "vehicle"
              ? {
                minHeight: 300,
                maxHeight: "calc(100vh - 232px)",
                height: "auto",
              }
              : { minHeight: 400 }
          }
          loading={summaryLoading}
          columns={columns}
          rows={allVehicles}
          paginationMode="client"
          getRowId={(row) => row.executionId}
          disableColumnMenu={false}
          {...(!vehicleId && { components: { Toolbar: CustomToolbar } })}
        />
      </Box>
    </Grid>
  );
};

export default VehicleDeploymentList;
